import { useEffect, useMemo, useState } from "react"
import { describeHelpers } from "../../../../../features/data-types"
import { Modal, ModalId, RadioField, Validatable, option } from "../../../../../features/ui"
import { Operation } from "../../../../operations"
import { HoardOrder, SaleOperation } from "../../../../sales"
import { hoardHelpers } from "../../../../sales/hooks/hoardHooks"
import { DeliveryOrder } from "../../../state/types"
import { DeliveryStatsTable } from "../../delivery-stats-table/deliveryStatsTable"
import { noteOptions } from "../../generate-note-modal/generateNoteModal"
import { INVOICE_IN_FLIGHT_MESSAGE, NoteGenerationType } from "../../types"
import { toList } from "../../../../../utils/listUtils"
import { useActions } from "./actions"

enum PendingItemsAction {
    KEEP = 'Keep',
    CANCEL = 'Cancel'
}

const pendingItemsActionOptions = [
    option(PendingItemsAction.KEEP, 'Dejarlos disponibles para entregar más adelante'),
    option(PendingItemsAction.CANCEL, 'Cancelarlos de la operación y generar Nota de Crédito')
]

type Props = {
    deliveryOrder?: DeliveryOrder
    operation: Operation
    onAnnul?: () => void
}

export const AnnulDeliveryOrderModal = (props: Props) => {
    const { deliveryOrder, operation, onAnnul } = props

    // Keeping the items available is the default to avoid cancelling them by mistake
    const [pendingItemsAction, setPendingItemsAction] = useState<PendingItemsAction>(PendingItemsAction.KEEP)
    const [noteType, setNoteType] = useState<NoteGenerationType>(NoteGenerationType.CREDIT_NOTE)

    const { describeCode } = describeHelpers

    const { submitAnnulDeliveryOrder, submitCancelPendingItems } = useActions()

    const hasInvoiceInFlight = useMemo(
        () => !!(operation as HoardOrder).invoiceInFlight,
        [operation]
    )

    // In a financial hoard annulling already frees the used credit, so there is nothing to choose
    const isFinancialHoard = useMemo(
        () => !!deliveryOrder && hoardHelpers.isFinancialHoard(deliveryOrder.table, operation as SaleOperation),
        [deliveryOrder, operation]
    )

    useEffect(() => {
        setPendingItemsAction(PendingItemsAction.KEEP)
        setNoteType(NoteGenerationType.CREDIT_NOTE)
    }, [deliveryOrder?.id, isFinancialHoard])

    const pendingItems = useMemo(
        () => (deliveryOrder?.items || []).filter(item => item.deliveryStats.pending > 0),
        [deliveryOrder?.items]
    )

    // El tipo de nota se ofrece solo si alguno de los pendientes ya está facturado.
    const hasInvoicedPendingQuantity = useMemo(
        () => !isFinancialHoard && pendingItems.some(item => {
            const operationItem = operation.items.find(operationItem => operationItem.id === item.itemId)
            return hoardHelpers.getInvoicedQuantity(operationItem, item.deliveryStats.pending) > 0
        }),
        [isFinancialHoard, pendingItems, operation]
    )

    const rows = useMemo(() => pendingItems.map(item => ({
        itemId: item.itemId,
        pending: item.deliveryStats.pending
    })), [pendingItems])

    const isCancel = pendingItemsAction === PendingItemsAction.CANCEL

    const onChangePendingItemsAction = (value?: string) => setPendingItemsAction(value as PendingItemsAction)

    const onChangeNoteType = (value?: string) => setNoteType(value as NoteGenerationType)

    const renderBody = () => (
        <>
            {hasInvoiceInFlight && (
                <Validatable validations={toList(INVOICE_IN_FLIGHT_MESSAGE)} expanded />
            )}
            <p>
                {deliveryOrder && `Está por anular la Orden de Entrega ${describeCode(deliveryOrder)}. `}
                {isFinancialHoard ?
                    'Los productos pendientes listados a continuación se descontarán del utilizado del acopio.' :
                    '¿Qué desea hacer con los productos pendientes listados a continuación?'}
                {' '}Esta acción es irreversible.
            </p>
            {deliveryOrder && pendingItems.length > 0 && (
                <DeliveryStatsTable
                    deliveryMethod={deliveryOrder.method}
                    items={rows}
                    operation={operation}
                    title="Productos"
                />
            )}
            {!isFinancialHoard && (
                <RadioField
                    id="pending-items-action"
                    value={pendingItemsAction}
                    options={pendingItemsActionOptions}
                    onChange={onChangePendingItemsAction}
                />
            )}
            {!isFinancialHoard && isCancel && hasInvoicedPendingQuantity && (
                <RadioField
                    id="annul-note-type"
                    value={noteType}
                    options={noteOptions}
                    onChange={onChangeNoteType}
                />
            )}
        </>
    )

    const onAnnulDeliveryOrder = async () => {
        if (!deliveryOrder || hasInvoiceInFlight) return
        if (!isFinancialHoard && isCancel) {
            await submitCancelPendingItems(deliveryOrder, hasInvoicedPendingQuantity ? noteType : NoteGenerationType.ADJUSTMENT_NOTE)
        } else {
            await submitAnnulDeliveryOrder(deliveryOrder.id)
        }
        onAnnul && onAnnul()
    }

    return (
        <Modal
            modalId={ModalId.ANNUL_DELIVERY_ORDER}
            title="Anular Orden de Entrega?"
            submitLabel="Anular"
            submitKind="secondary"
            widthSize="m"
            disableSubmit={hasInvoiceInFlight}
            onSubmit={onAnnulDeliveryOrder}
        >
            {renderBody()}
        </Modal>
    )
}
